import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Box } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoDisturbAltIcon from '@mui/icons-material/DoDisturbAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  AccountAction,
  AccountActionLink,
  AccountLinks,
} from '../../../../domain/models/AccountStatus';
import { AccountActionConfirmDialog } from './AccountActionConfirmDialog';

/**
 * Props for AccountActionButtons component
 */
interface AccountActionButtonsProps {
  links: AccountLinks;
  accountName: string;
  onAction: (actionLink: AccountActionLink, actionType: AccountAction) => Promise<void>;
}

/**
 * Map action to corresponding MUI icon, label, and color
 */
const actionConfig: Record<
  AccountAction,
  { icon: React.ElementType; label: string; color?: 'default' | 'error' }
> = {
  verify: { icon: CheckIcon, label: 'Verify', color: 'default' },
  activate: { icon: DoneAllIcon, label: 'Activate', color: 'default' },
  cancel: { icon: DoDisturbAltIcon, label: 'Cancel', color: 'error' },
  remove: { icon: DeleteIcon, label: 'Remove', color: 'error' },
};

/**
 * Destructive actions that require confirmation
 */
const DESTRUCTIVE_ACTIONS: AccountAction[] = ['cancel', 'remove'];

/**
 * AccountActionButtons Component
 * Displays a dropdown menu with actions dynamically built from backend-provided links
 * Shows confirmation dialog for destructive actions (cancel, remove)
 */
export const AccountActionButtons: React.FC<AccountActionButtonsProps> = ({
  links,
  accountName,
  onAction,
}) => {
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: AccountAction | null;
    actionLink: AccountActionLink | null;
  }>({
    open: false,
    action: null,
    actionLink: null,
  });
  const open = Boolean(anchorEl);

  // Extract available actions from links (exclude 'self')
  const availableActions = Object.entries(links)
    .filter(([key, value]) => key !== 'self' && value !== undefined)
    .map(([key, link]) => ({
      action: key as AccountAction,
      link: link as AccountActionLink,
    }));

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleActionClick = (action: AccountAction, actionLink: AccountActionLink) => {
    // Close menu first
    handleMenuClose();

    // Show confirmation for destructive actions
    if (DESTRUCTIVE_ACTIONS.includes(action)) {
      setConfirmDialog({
        open: true,
        action,
        actionLink,
      });
    } else {
      // Execute immediately for non-destructive actions
      void onAction(actionLink, action);
    }
  };

  const handleConfirmAction = () => {
    if (confirmDialog.action && confirmDialog.actionLink) {
      void onAction(confirmDialog.actionLink, confirmDialog.action);
    }
    setConfirmDialog({ open: false, action: null, actionLink: null });
  };

  const handleCancelConfirm = () => {
    setConfirmDialog({ open: false, action: null, actionLink: null });
  };

  return (
    <>
      <Box
        sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '100%' }}
      >
        <IconButton
          size="small"
          aria-label="Open actions menu"
          aria-controls={open ? 'actions-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleMenuOpen}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          id="actions-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          slotProps={{
            paper: {
              sx: { minWidth: 150 },
            },
          }}
        >
          {availableActions.map(({ action, link }) => {
            const config = actionConfig[action];
            const IconComponent = config.icon;
            const isDestructive = config.color === 'error';

            return (
              <MenuItem
                key={action}
                onClick={() => handleActionClick(action, link)}
                aria-label={`${config.label} account`}
                sx={isDestructive ? { color: 'error.main' } : undefined}
              >
                <ListItemIcon>
                  <IconComponent fontSize="small" color={config.color} />
                </ListItemIcon>
                <ListItemText>{config.label}</ListItemText>
              </MenuItem>
            );
          })}
        </Menu>
      </Box>

      {confirmDialog.action && (
        <AccountActionConfirmDialog
          open={confirmDialog.open}
          action={confirmDialog.action}
          accountName={accountName}
          onConfirm={handleConfirmAction}
          onCancel={handleCancelConfirm}
        />
      )}
    </>
  );
};
